import api from "@/lib/api";
import { toast } from "sonner";

const loadScript = (src) =>
  new Promise((resolve) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
    const s = document.createElement("script");
    s.src = src;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

// Advance-payment checkout. Confirmation happens ONLY after backend verification.
export const payForBooking = async (bookingId, onSuccess) => {
  try {
    const cfg = await api.get("/payments/config");
    if (!cfg.data.configured) {
      toast.info("Online payments are being set up. Razorpay keys will be added shortly — you can pay your advance here once it's live.");
      return;
    }
    const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!ok) return toast.error("Could not load the payment gateway. Please check your connection.");
    const { data } = await api.post("/payments/create-order", { booking_id: bookingId });
    const rzp = new window.Razorpay({
      key: data.key_id,
      amount: data.amount,
      currency: data.currency,
      order_id: data.order_id,
      name: "Avinya Bookings",
      description: `Advance for ${data.booking_ref}`,
      prefill: { name: data.name, email: data.email, contact: data.contact },
      notes: { booking_ref: data.booking_ref },
      theme: { color: "#166534" },
      handler: async (res) => {
        try {
          const v = await api.post("/payments/verify", {
            booking_id: bookingId,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          toast.success("Payment successful! Your booking is confirmed.");
          onSuccess?.(v.data);
        } catch (e) {
          toast.error("Payment verification failed. Please try again or contact support.");
          onSuccess?.(null);
        }
      },
      modal: {
        ondismiss: () => toast.info("Payment cancelled. Your booking is still reserved — pay the advance before the window closes."),
      },
    });
    rzp.on("payment.failed", () => {
      toast.error("Payment was unsuccessful. Please try again.");
      onSuccess?.(null);
    });
    rzp.open();
  } catch (e) {
    const d = e.response?.data?.detail;
    toast.error(typeof d === "string" ? d : "Could not start payment. Please try again.");
  }
};

// Balance (remaining amount) online payment — same secure verify flow.
export const payBalanceForBooking = async (bookingId, onSuccess) => {
  try {
    const cfg = await api.get("/payments/config");
    if (!cfg.data.configured) {
      toast.info("Online payments are being set up. You'll be able to pay the balance here once Razorpay is live.");
      return;
    }
    const ok = await loadScript("https://checkout.razorpay.com/v1/checkout.js");
    if (!ok) return toast.error("Could not load the payment gateway. Please check your connection.");
    const { data } = await api.post("/payments/create-balance-order", { booking_id: bookingId });
    const rzp = new window.Razorpay({
      key: data.key_id,
      amount: data.amount,
      currency: data.currency,
      order_id: data.order_id,
      name: "Avinya Bookings",
      description: `Balance for ${data.booking_ref}`,
      prefill: { name: data.name, email: data.email, contact: data.contact },
      theme: { color: "#166534" },
      handler: async (res) => {
        try {
          const v = await api.post("/payments/verify-balance", {
            booking_id: bookingId,
            razorpay_order_id: res.razorpay_order_id,
            razorpay_payment_id: res.razorpay_payment_id,
            razorpay_signature: res.razorpay_signature,
          });
          toast.success("Balance paid! Your booking is fully settled.");
          onSuccess?.(v.data);
        } catch (e) {
          toast.error("Payment verification failed. Please try again or contact support.");
          onSuccess?.(null);
        }
      },
      modal: { ondismiss: () => toast.info("Balance payment cancelled. You can pay it anytime before your event.") },
    });
    rzp.on("payment.failed", () => toast.error("Payment was unsuccessful. Please try again."));
    rzp.open();
  } catch (e) {
    const d = e.response?.data?.detail;
    toast.error(typeof d === "string" ? d : "Could not start payment. Please try again.");
  }
};
